import { GridReadyEvent } from '@ag-grid-community/core';
import { Component } from '@angular/core';
import { Manufacturer, PoHeader } from 'app/model/entities/entity-model';
import { AgFns, GridOptionsExt } from 'app/shared/ag-fns';

import { PoStatusCode } from 'app/model/enums/po-status-code';
import { DateFns } from 'app/shared/date-fns';
import { DialogBaseComponent, DialogParams } from 'app/shared/dialog-base.component';
import { DomainService } from 'app/shared/domain.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

export interface CombinePurchaseOrdersDialogParams extends DialogParams {
  // empty product means to add one - in which case productType must be entered..
  destPoHeader: PoHeader;
  manufacturer: Manufacturer;
}

@Component({
  templateUrl: './combine-purchase-orders-dialog.component.html',
  standalone: false,
})
export class CombinePurchaseOrdersDialogComponent extends DialogBaseComponent<CombinePurchaseOrdersDialogParams> {
  poGridOptions: GridOptionsExt;
  poHeaders: PoHeader[];
  isSaving = false;
  shouldCombine = '_shouldCombine';
  destPoHeader: PoHeader;

  constructor(
    public config: DynamicDialogConfig<CombinePurchaseOrdersDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService,
  ) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, {
      header: 'Select purchase orders to combine',
      width: '700px',
      height: '480px',
    });
    this.prepare();
  }

  async prepare() {
    this.destPoHeader = this.params.destPoHeader;
    this.poGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPoGridReady,
    });

    await this.getPurchaseOrders();
    this.isPageReady = true;
  }

  async getPurchaseOrders() {
    if (this.destPoHeader.poStatusId !== PoStatusCode.NewUnposted) {
      await this.dialogService.showOkMessage(
        'Invalid Operation',
        'You cannot combine onto a purchaseOrder that has a status of: ' + this.destPoHeader.poStatus.name,
      );
      this.dialogRef.close(null);
      return;
    }

    const poHeaders = await this.dbQueryService.getUnpostedPoHeadersForManufacturer(this.params.manufacturer.id);
    this.poHeaders = poHeaders.filter((po) => po.id !== this.destPoHeader.id);
    this.poHeaders.forEach((po) => (po[this.shouldCombine] = null));
  }

  onPoGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        ...AgFns.createCheckboxProps('Combine', this.shouldCombine, 'combineCheckBox'),
        valueGetter: (p) => !!p.data[this.shouldCombine],
        valueSetter: (p) => (p.data[this.shouldCombine] = p.newValue),
        editable: true,
      },
      { headerName: 'Purchase Order', field: 'id', filter: 'agNumberColumnFilter' },
      { headerName: 'Manufacturer', field: 'manufacturer.name', filter: 'agTextColumnFilter' },
      { headerName: 'P/O Date', field: 'poDate', filter: 'agDateColumnFilter' },
    ];
    AgFns.initGrid(event, colDefs);
    AgFns.autoSizeAllColumns(this.poGridOptions);
  }

  async ok() {
    if (this.isSaving) return false;
    this.isSaving = true;
    try {
      return await this.combine();
    } finally {
      this.isSaving = false;
    }
  }

  private async combine() {
    this.poGridOptions.apiX.stopEditing();

    await this.dbQueryService.checkRowVersion(this.destPoHeader);

    const combinedPos = this.poHeaders.filter((po) => po[this.shouldCombine]);
    if (combinedPos.length === 0) {
      await this.dialogService.showOkMessage('No Selection', 'Please select at least one purchase order to combine.');
      return false;
    }

    // Check for account-owned POs in selection
    const accountOwnedPoIds = new Set(
      await this.dbQueryService.getAccountOwnedPoHeaderIds(this.params.manufacturer.id),
    );
    const accountOwnedSelections = combinedPos.filter((po) => accountOwnedPoIds.has(po.id));
    if (accountOwnedSelections.length > 0) {
      const poList = accountOwnedSelections.map((po) => po.id).join(', ');
      await this.dialogService.showOkMessage(
        'Account-Owned Purchase Orders',
        `The following selected purchase orders are account-owned and cannot be combined: ${poList}`,
      );
      return false;
    }

    combinedPos.slice().forEach((po) => {
      // move poDetails to dest
      po.poNotes.slice().forEach((pon) => (pon.poHeaderId = this.destPoHeader.id));
      po.poDetails.slice().forEach((pod) => {
        pod.poHeaderId = this.destPoHeader.id;
        pod.modTs = new Date();
      });
      // then delete po
      po.entityAspect.setDeleted();
    });

    // poke crtn ts by 1 second for Optimistic concurrency
    this.destPoHeader.crtnTs = DateFns.pokeDate(this.destPoHeader.crtnTs);

    await this.dbSaveService.saveChanges();
    this.dialogRef.close(true);
    return true;
  }

  cancel() {
    this.dialogRef.close(null);
  }
}
